"use client";

import React from 'react';
import Link from 'next/link';
import { ArrowLeft, Star, Heart, Crown, Shield, RotateCw, Zap, Moon, Eye, TrendingUp } from 'lucide-react';
import { AboutLayout } from '@/components/about/about-layout';

export default function LaRoueDeFortunePage() {
  return (
    <AboutLayout activeSection="tarot">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Current Section Indicator */}
        <div className="mb-4 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
          <div className="flex items-center gap-2 text-yellow-300">
            <Star className="h-4 w-4" />
            <span className="text-sm font-medium">Tarot</span>
            <span className="text-yellow-500">/</span>
            <span className="text-sm">Marseille</span>
            <span className="text-yellow-500">/</span>
            <span className="text-sm">Major Arkana</span>
            <span className="text-yellow-500">/</span>
            <span className="text-sm">La Roue de Fortune</span>
          </div>
        </div>

        {/* Breadcrumb */}
        <div className="mb-6">
          <Link
            href="/about"
            className="inline-flex items-center text-slate-400 hover:text-slate-200 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            AXTROLOG Nedir
          </Link>
          <span className="text-slate-500 mx-2">/</span>
          <Link
            href="/about/tarot/systems/marseille"
            className="text-slate-400 hover:text-slate-200 transition-colors"
          >
            Tarot de Marseille
          </Link>
          <span className="text-slate-500 mx-2">/</span>
          <Link
            href="/about/tarot/systems/marseille/major-arcana"
            className="text-slate-400 hover:text-slate-200 transition-colors"
          >
            Major Arkana
          </Link>
          <span className="text-slate-500 mx-2">/</span>
          <span className="text-slate-300">La Roue de Fortune</span>
        </div>

        {/* Header with Card */}
        <div className="bg-gradient-to-r from-green-900/20 to-yellow-900/20 rounded-xl p-8 mb-8 border border-green-500/20">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
            <div className="lg:col-span-1 flex justify-center">
              <div className="w-48 h-72 rounded-xl overflow-hidden shadow-2xl">
                <img
                  src="/images/tarot/cards/marseille-la-roue-de-fortune.svg"
                  alt="La Roue de Fortune Tarot Card"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    const target = e.currentTarget;
                    target.style.display = 'none';
                    if (target.nextElementSibling) {
                      (target.nextElementSibling as HTMLElement).style.display = 'flex';
                    }
                  }}
                />
                <div className="hidden w-full h-full bg-gradient-to-b from-green-900/40 to-yellow-900/40 border border-green-500/30 flex-col items-center justify-center text-green-300">
                  <span className="text-3xl font-bold mb-2">X</span>
                  <span className="text-lg font-semibold">LA ROUE DE FORTUNE</span>
                  <span className="text-sm text-center mt-2">Wheel of Fortune</span>
                </div>
              </div>
            </div>
            <div className="lg:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-yellow-700 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold text-lg">X</span>
                </div>
                <div>
                  <h1 className="text-4xl font-bold text-slate-100">La Roue de Fortune</h1>
                  <p className="text-xl text-green-400">Talih Tekerlegi / Kader ve Döngü</p>
                </div>
              </div>
              <p className="text-lg text-slate-200 leading-relaxed mb-4">
                Marseille geleneğinde onuncu numara. Dönmeye devam eden talih tekerlegi,
                kaderin döngüselliği, değişim ve karma yasasını temsil eder. Yaşamın döner
                doğasını gösteren karttır.
              </p>
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 bg-green-500/20 text-green-300 rounded-full text-sm">Kader</span>
                <span className="px-3 py-1 bg-green-500/20 text-green-300 rounded-full text-sm">Döngü</span>
                <span className="px-3 py-1 bg-green-500/20 text-green-300 rounded-full text-sm">Değişim</span>
                <span className="px-3 py-1 bg-green-500/20 text-green-300 rounded-full text-sm">Karma</span>
              </div>
            </div>
          </div>
        </div>

        {/* Card Meanings */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Positive Meanings */}
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <h2 className="text-2xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
              <RotateCw className="h-6 w-6 text-green-400" />
              Düz Pozisyon Anlamları
            </h2>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Olumlu Değişim</h3>
                  <p className="text-slate-400 text-sm">Yaşama gelen iyi dönüşümler, şans faktörü ve fırsat.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Kaderin Yardımı</h3>
                  <p className="text-slate-400 text-sm">Evrenin desteği, doğru zamanlama ve mükemmel tesadüfler.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Yeni Fırsat</h3>
                  <p className="text-slate-400 text-sm">Beklenmedik imkanlar, yeni başlangıçlar ve gelişim.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Karma Ödemesi</h3>
                  <p className="text-slate-400 text-sm">İyilik yapan iyilik bulur, geçmiş emeklerinin karşılığı.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Negative Meanings */}
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <h2 className="text-2xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
              <Moon className="h-6 w-6 text-red-400" />
              Ters Pozisyon Anlamları
            </h2>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Kötü Talih</h3>
                  <p className="text-slate-400 text-sm">Olumsuz dönüşümler, güçsüzlük ve engellemeler.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Gecikme</h3>
                  <p className="text-slate-400 text-sm">Zamanlamadaki aksaklıklar, beklemek zorunda kalma.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Kontrolsüzlük</h3>
                  <p className="text-slate-400 text-sm">Olaylara hakim olamama, dışsal güçlerle mücadele.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Olumsuz Karma</h3>
                  <p className="text-slate-400 text-sm">Geçmişin hatalarının bedelini ödeme, olumsuz geri dönüş.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Symbolism */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Marseille Sembolizmi</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <RotateCw className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Tekerlek</h3>
              <p className="text-slate-400 text-sm">Sonsuz döngü, yaşamın ritmi ve kaderin çevirimi.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <TrendingUp className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Yükselme</h3>
              <p className="text-slate-400 text-sm">İleriye doğru hareket, gelişim ve yükseliş.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">↓</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Düşme</h3>
              <p className="text-slate-400 text-sm">Aşağı yönelim, kayıp ve düşüş.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-yellow-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <Zap className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Dinamizm</h3>
              <p className="text-slate-400 text-sm">Sürekli hareket, enerji ve değişken durum.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-orange-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <Eye className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Kaderin Gözü</h3>
              <p className="text-slate-400 text-sm">Üstün güç, evrensel adalet ve denetim.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">∞</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Sonsuzluk</h3>
              <p className="text-slate-400 text-sm">Döngüsellik, süreklilik ve zamansızlık.</p>
            </div>
          </div>
        </div>

        {/* Life Areas */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Yaşam Alanlarındaki Anlamlar</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Heart className="h-5 w-5 text-pink-400" />
                Aşk ve İlişkiler
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Kaderin getirdiği karşılaşmalar</li>
                <li>• İlişki döngülerinin tamamlanması</li>
                <li>• Yeni aşk fırsatları</li>
                <li>• Karmik bağlantılar</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-green-400" />
                Kariyer ve İş
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Beklenmedik iş fırsatları</li>
                <li>• Maddi durum değişimleri</li>
                <li>• Yeni projeler ve girişimler</li>
                <li>• Profesyonel dönüşüm</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Crown className="h-5 w-5 text-yellow-400" />
                Kişisel Gelişim
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Yaşamdaki dersler</li>
                <li>• Karakter olgunlaşması</li>
                <li>• Sabır ve kabul etme</li>
                <li>• Adaptasyon yetisi</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Shield className="h-5 w-5 text-blue-400" />
                Manevi Gelişim
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Karma yasasının anlaşılması</li>
                <li>• Evrensel düzenle uyum</li>
                <li>• Kaderci yaklaşım</li>
                <li>• Ruhsal dönüşüm</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Important Note */}
        <div className="bg-amber-900/20 border border-amber-700/50 rounded-xl p-6 mb-8">
          <h3 className="text-lg font-semibold text-amber-200 mb-3">⚠️ Önemli Uyarı</h3>
          <p className="text-amber-100 leading-relaxed">
            Bu kart anlamları geleneksel Marseille tarot yorumlarına dayanmaktadır ve sadece eğlence ve kişisel farkındalık amaçlıdır.
            Yaşamsal kararlar için uzman görüşü alınmalıdır.
          </p>
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <Link
            href="/about/tarot/systems/marseille/major-arcana/l-ermite"
            className="inline-flex items-center px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-lg text-slate-300 hover:text-slate-100 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            L'Ermite
          </Link>

          <Link
            href="/about/tarot/systems/marseille/major-arcana/la-force"
            className="inline-flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
          >
            La Force
            <ArrowLeft className="h-4 w-4 ml-2 rotate-180" />
          </Link>
        </div>
      </div>
    </AboutLayout>
  );
}